import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useIngredientSearchData from 'components/desktop/Ingredient/Search/useIngredientSearchData';
import CharacteristicToggleSelectNew from 'components/desktop/_general/CharacteristicSelect/CharacteristicToggleSelectNew';
import Co2Select from 'components/desktop/_general/Co2Select/Co2SelectNew';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import MultiSearchSelectNew from 'components/desktop/_general/MultiSearchSelect/MultiSearchSelectNew';
import NutriScoreDiagramSelectNew from 'components/desktop/_general/NutriScoreSelect/NutrieScoreDiagramSelectNew';
import RangeDouble from 'components/desktop/_general/RangeDouble/RangeDoubleNew';
import SingleSearchSelectNew from 'components/desktop/_general/Select/SingleSearchSelect/SingleSearchSelectNew';
import ENTITLEMENTS from 'enums/entitlements';
import * as AllergenList from 'types/Ingredient/Search/AllergenList';
import * as IngredientSearchResponse from 'types/Ingredient/Search/IngredientSearchResponseDto';
import * as RangeWithIdList from 'types/Ingredient/Search/RangeWithIdList';
import * as Search from 'types/Ingredient/Search/Search';
import * as SliderWithIdList from 'types/Ingredient/Search/SliderWithIdList';
import * as NutriScoreLabelList from 'types/NutriScore/NutriScoreLabelList';
import * as CharacteristicToggleList from 'types/_general/Search/CharacteristicToggleList';
import * as Range from 'types/_general/Search/Range';
import * as Slider from 'types/_general/Search/Slider';
import * as BaseIngredientList from 'types/_general/Store/BaseIngredientList';

interface IProps {
	disabled: boolean;
	handleChangeAllergens: (input: string[]) => void;
	handleChangeBaseIngredient: (input: string | null) => void;
	handleChangeCategories: (items: string[]) => void;
	handleChangeCharacteristics: (input: { saved: string; all: string[] }) => void;
	handleChangeCo2Labels: (id: string) => void;
	handleChangeNutriScoreLabels: (input: { saved: string; all: NutriScoreLabelList.Type }) => void;
	handleChangeSliders: (input: {
		id: string;
		left: number | null;
		right: number | null;
		maximum: number | null;
		minimum: number | null;
	}) => void;
	handleChangeSeasons: (items: string[]) => void;
	handleChangeTags: (items: string[]) => void;
	handleReset: () => void;
	search: Search.Type;
	searchResponse: IngredientSearchResponse.Type;
}

const AdvancedFilters = (props: IProps) => {
	const { t } = useTranslation();
	const initialCharacteristics = useMemo(() => CharacteristicToggleList.initialize(), []);
	const data = useIngredientSearchData();

	const renderCol1 = (): JSX.Element => {
		return (
			<>
				<SingleSearchSelectNew
					allItems={BaseIngredientList.mapToSelectItemList(data.baseIngredientList)}
					className="mb-2"
					errorKey="BaseIngredient"
					label={t('_general:BASEINGREDIENT')}
					savedItem={props.search.baseIngredientId}
					setSavedItem={props.handleChangeBaseIngredient}
				/>
				<MultiSearchSelectNew
					allItems={data.tags}
					className="mb-2"
					displaySavedItems="top"
					errorKey="Tags"
					label={t('_general:TAGS')}
					savedItems={props.search.tagIds}
					setSavedItems={props.handleChangeTags}
				/>
				<MultiSearchSelectNew
					allItems={AllergenList.mapToSelectItemList(data.allergens)}
					className="mb-2"
					displaySavedItems="top"
					errorKey="Allergens"
					label={t('_general:ALLERGENS')}
					savedItems={props.search.allergenIds}
					setSavedItems={props.handleChangeAllergens}
				/>
				<RenderIf entitlements={[ENTITLEMENTS.SEASON]}>
					<MultiSearchSelectNew
						allItems={data.seasons}
						className="mb-2"
						displaySavedItems="top"
						errorKey="Seasons"
						label={t('_general:SEASONS')}
						savedItems={props.search.seasonIds}
						setSavedItems={props.handleChangeSeasons}
					/>
				</RenderIf>
				<MultiSearchSelectNew
					allItems={data.ingredientCategories}
					className="mb-2"
					displaySavedItems="top"
					errorKey="Categories"
					label={t('_general:CATEGORIES')}
					savedItems={props.search.categoryIds}
					setSavedItems={props.handleChangeCategories}
				/>
			</>
		);
	};

	const renderCol2 = (): JSX.Element => {
		return (
			<div className="d-flex flex-column" style={{ gap: '15px' }}>
				{data.ranges.map((range, i) => {
					return (
						<div key={i} className="row">
							<div className="col-3">
								<label className="">{range.label}</label>
							</div>
							<div className="col-9">
								<RangeDoubleLabelWrapper
									handleChange={(left, right) => {
										return props.handleChangeSliders({
											...range,
											left: left,
											right: right,
										});
									}}
									label={range.label}
									rangeArea={RangeWithIdList.getById(
										props.searchResponse.ranges,
										range.id
									)}
									rangeMinMax={range}
									slider={SliderWithIdList.getById(
										props.search.sliders,
										range.id
									)}
								/>
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	const renderCol3 = (): JSX.Element => {
		return (
			<div className="d-flex flex-column" style={{ gap: '10px' }}>
				<RenderIf entitlements={[ENTITLEMENTS.CARBONDIOXIDE]}>
					<Co2Select
						carbonDioxideLabels={data.co2Labels}
						activeIds={props.search.co2LabelIds}
						setActiveId={props.handleChangeCo2Labels}
					/>
				</RenderIf>
				<RenderIf entitlements={[ENTITLEMENTS.NUTRI_SCORE]}>
					<NutriScoreDiagramSelectNew
						all={data.nutriScoreLabels}
						handleChange={props.handleChangeNutriScoreLabels}
						saved={props.search.nutriScoreLabelIds}
					/>
				</RenderIf>
				<CharacteristicToggleSelectNew
					all={initialCharacteristics}
					handleChange={props.handleChangeCharacteristics}
					saved={props.search.characteristics}
				/>
			</div>
		);
	};

	return (
		<>
			<div className="row">
				<div className="col-lg-4">{renderCol1()}</div>
				<div className="col-lg-6">{renderCol2()}</div>
				<div className="col-lg-2">{renderCol3()}</div>
			</div>
			<div className="row">
				<div className="col-lg-12" style={{ textAlign: 'end' }}>
					<button className="btn btn-primary" onClick={props.handleReset}>
						{t('_general:RESET')}
					</button>
				</div>
			</div>
		</>
	);
};

export default AdvancedFilters;

const RangeDoubleLabelWrapper = (props: {
	rangeArea: Range.Type;
	rangeMinMax: Range.Type;
	slider: Slider.Type;
	label: string;
	handleChange: (left: number | null, right: number | null) => void;
}) => {
	const { rangeArea, rangeMinMax, slider } = props;
	return (
		<RangeDouble
			areaMax={rangeArea.maximum ?? rangeMinMax.maximum ?? 0}
			areaMin={rangeArea.minimum ?? rangeMinMax.minimum ?? 0}
			defaultControlLeft={slider.left ?? rangeMinMax.minimum ?? 0}
			defaultControlRight={slider.right ?? rangeMinMax.maximum ?? 0}
			handleChange={props.handleChange}
			totalMax={Slider.getMaxRoundUp(rangeMinMax.maximum ?? 0)}
			totalMin={Slider.getMinRoundDown(rangeMinMax.minimum ?? 0)}
		/>
	);
};
